import type { Bridge, BrowsedService } from '@casa/bridge';
import type { DriverDiscovery } from '@casa/driver-sdk';

// Drivers' discovery (dashboard-editor spec M-8): the app browses for the service type a driver's
// manifest declares, and offers each system found with the configuration its address fills. Adding
// a connection (E5) offers these; the drivers part of the settings lists them meanwhile.

/** How long to look; systems answer within a second or two. */
export const DISCOVERY_MS = 3_000;

export interface FoundSystem {
    /** As the system announces itself: "p1meter-3E233E", "Hubitat Belgium". */
    readonly name: string;
    /** Its IPv4 address where it has one: a `.local` name does not resolve on every device. */
    readonly address: string;
    /** What it fills in a new connection's configuration: `{ host: '10.27.222.98' }`. */
    readonly config: Readonly<Record<string, string>>;
}

export function toFoundSystem(service: BrowsedService, discovery: DriverDiscovery) : FoundSystem {
    const address = service.addresses.find((candidate) => !candidate.includes(':')) ?? service.addresses[0] ?? service.host;

    return { name: service.name, address, config: { [discovery.configField]: address } };
}

/** The systems of a driver's kind on the LAN, sorted by name. Rejects where the device cannot browse. */
export async function discoverSystems(bridge: Bridge, discovery: DriverDiscovery, timeoutMs = DISCOVERY_MS) : Promise<FoundSystem[]> {
    const services = await bridge.net.browse(discovery.mdns, { timeoutMs });

    return services.map((service) => toFoundSystem(service, discovery)).sort((a, b) => a.name.localeCompare(b.name));
}
